/**
 * JobsBoard Component
 * Main jobs board UI with tabs: Browse Jobs, My Applications, My Profile, Post Job (for employers)
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import CyberSpinner from './CyberSpinner';
import { 
  jobsService, 
  Job, 
  JobApplication, 
  WorkerProfile, 
  Company,
  BASINS,
  JOB_TYPES 
} from '@services/jobsService';
import { telegram } from '@services/telegram';

// Import sub-components (will create these next)
import { JobCard } from './jobs/JobCard';
import { JobDetail } from './jobs/JobDetail';
import { WorkerProfileForm } from './jobs/WorkerProfileForm';
import { CompanyRegistration } from './jobs/CompanyRegistration';
import { JobPostForm } from './jobs/JobPostForm';

interface JobsBoardProps {
  open: boolean;
  onClose: () => void;
}

type TabValue = 'browse' | 'applications' | 'profile' | 'employer';

export function JobsBoard({ open, onClose }: JobsBoardProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('browse');
  const [loading, setLoading] = useState(true);
  
  // Jobs state
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [basinFilter, setBasinFilter] = useState<string>('');
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  
  // Applications state
  const [applications, setApplications] = useState<JobApplication[]>([]);
  
  // Profile state
  const [profile, setProfile] = useState<WorkerProfile | null>(null);
  const [hasProfile, setHasProfile] = useState(false);
  
  // Company/Employer state
  const [company, setCompany] = useState<Company | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isEmployer, setIsEmployer] = useState(false);
  const [myPostedJobs, setMyPostedJobs] = useState<Job[]>([]);
  const [showJobPostForm, setShowJobPostForm] = useState(false);
  const [showCompanyRegistration, setShowCompanyRegistration] = useState(false);

  // Load initial data
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  // Reload jobs when filters change
  useEffect(() => {
    if (open && activeTab === 'browse') {
      loadJobs();
    }
  }, [searchQuery, basinFilter, jobTypeFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadJobs(),
        loadApplications(),
        loadProfile(),
        loadCompany()
      ]);
    } catch (error) {
      console.error('Failed to load jobs data:', error);
    }
    setLoading(false);
  };

  const loadJobs = async () => {
    const result = await jobsService.searchJobs({
      q: searchQuery || undefined,
      basin: basinFilter || undefined,
      jobType: jobTypeFilter || undefined,
      limit: 50
    });
    setJobs(result.jobs);
    setJobsTotal(result.total);
  };

  const loadApplications = async () => {
    const apps = await jobsService.getMyApplications();
    setApplications(apps);
  };

  const loadProfile = async () => {
    const result = await jobsService.getMyProfile();
    setProfile(result.profile);
    setHasProfile(result.hasProfile);
  };

  const loadCompany = async () => {
    const comp = await jobsService.getMyCompany();
    setCompany(comp);
    setIsEmployer(comp?.status === 'verified');
    
    if (comp?.status === 'verified') {
      const posted = await jobsService.getMyPostedJobs();
      setMyPostedJobs(posted);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
    telegram.hapticFeedback('light');
  };

  const handleJobClick = (job: Job) => {
    setSelectedJob(job);
    telegram.hapticFeedback('light');
  };

  const handleApply = async (job: Job, message?: string) => {
    telegram.hapticFeedback('medium');
    const result = await jobsService.applyForJob(job.id, message);
    
    if (result.success) {
      telegram.showAlert('Application submitted successfully!');
      loadApplications();
      setSelectedJob(null);
    } else {
      telegram.showAlert(result.error || 'Failed to apply');
    }
  };

  const handleProfileSaved = () => {
    loadProfile();
    telegram.hapticFeedback('success');
  };

  const handleCompanyRegistered = () => {
    loadCompany();
    setShowCompanyRegistration(false);
    telegram.hapticFeedback('success');
  };

  const handleJobPosted = () => {
    loadJobs();
    loadCompany();
    setShowJobPostForm(false);
    telegram.hapticFeedback('success');
  };

  // Render tab content
  const renderBrowseTab = () => (
    <Box>
      {/* Search and Filters */}
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search jobs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'rgba(255,255,255,0.05)',
              '& input': { color: 'white' }
            }
          }}
        />
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Basin</InputLabel>
            <Select
              value={basinFilter}
              onChange={(e) => setBasinFilter(e.target.value)}
              label="Basin"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: 'white',
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <MenuItem value="">All Basins</MenuItem>
              {BASINS.map(basin => (
                <MenuItem key={basin} value={basin}>{basin}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Type</InputLabel>
            <Select
              value={jobTypeFilter}
              onChange={(e) => setJobTypeFilter(e.target.value)}
              label="Type"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: 'white',
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <MenuItem value="">All Types</MenuItem>
              {JOB_TYPES.map(type => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Results count */}
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
        {jobsTotal} jobs found
      </Typography>

      {/* Job list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {jobs.length === 0 ? (
          <Alert severity="info" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
            No jobs found matching your criteria
          </Alert>
        ) : (
          jobs.map(job => (
            <JobCard 
              key={job.id} 
              job={job} 
              onClick={() => handleJobClick(job)}
            />
          ))
        )}
      </Box>
    </Box>
  );

  const renderApplicationsTab = () => (
    <Box>
      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
        My Applications ({applications.length})
      </Typography>

      {applications.length === 0 ? (
        <Alert severity="info" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
          You haven't applied to any jobs yet. Browse jobs and apply!
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {applications.map(app => (
            <Card 
              key={app.id}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.08)'
                }
              }}
              onClick={() => app.job && handleJobClick(app.job)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                      {app.job?.title || 'Job Title'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {app.job?.company || 'Company'}
                    </Typography>
                  </Box>
                  <Chip 
                    label={app.status}
                    size="small"
                    color={
                      app.status === 'contacted' ? 'success' :
                      app.status === 'viewed' ? 'info' :
                      app.status === 'rejected' ? 'error' :
                      'default'
                    }
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, display: 'block' }}>
                  Applied: {new Date(app.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderProfileTab = () => (
    <Box>
      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
        Worker Profile
      </Typography>

      {!hasProfile ? (
        <Alert severity="info" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', mb: 2 }}>
          Create your profile to apply for jobs and get discovered by employers
        </Alert>
      ) : null}

      <WorkerProfileForm 
        profile={profile}
        onSaved={handleProfileSaved}
      />
    </Box>
  );

  const renderEmployerTab = () => (
    <Box>
      {!company ? (
        // No company registered
        <Box>
          <Alert severity="info" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', mb: 2 }}>
            Register your company to post jobs and search for workers
          </Alert>
          <Button
            variant="contained"
            startIcon={<BusinessIcon />}
            onClick={() => setShowCompanyRegistration(true)}
            sx={{ 
              backgroundColor: '#3b82f6',
              '&:hover': { backgroundColor: '#2563eb' }
            }}
          >
            Register Company
          </Button>
        </Box>
      ) : company.status === 'pending' ? (
        // Company pending verification
        <Box>
          <Alert severity="warning" sx={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', mb: 2 }}>
            Your company is pending email verification. Check your inbox for the verification code.
          </Alert>
          <CompanyRegistration 
            company={company}
            onVerified={handleCompanyRegistered}
          />
        </Box>
      ) : (
        // Verified company - employer dashboard
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'white' }}>
                {company.name}
              </Typography>
              <Chip 
                label="Verified" 
                size="small" 
                color="success" 
                icon={<CheckCircleIcon />}
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowJobPostForm(true)}
              sx={{ 
                backgroundColor: '#22c55e',
                '&:hover': { backgroundColor: '#16a34a' }
              }}
            >
              Post Job
            </Button>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Card sx={{ 
              flex: 1, 
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ color: '#3b82f6' }}>
                  {company.activeJobCount || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Active Jobs
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Posted Jobs */}
          <Typography variant="subtitle1" sx={{ color: 'white', mb: 1 }}>
            Your Posted Jobs
          </Typography>

          {myPostedJobs.length === 0 ? (
            <Alert severity="info" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
              You haven't posted any jobs yet
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {myPostedJobs.map(job => (
                <Card 
                  key={job.id}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: 'white' }}>
                          {job.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip 
                            icon={<VisibilityIcon />}
                            label={`${job.views_count || 0} views`}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)' }}
                          />
                          <Chip 
                            icon={<GroupIcon />}
                            label={`${job.applications_count || 0} applications`}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)' }}
                          />
                        </Box>
                      </Box>
                      <Chip 
                        label={job.status}
                        size="small"
                        color={job.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: '#0f172a',
            backgroundImage: 'linear-gradient(to bottom, #0f172a, #1e293b)',
            maxHeight: '100vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon sx={{ color: '#3b82f6' }} />
            <Typography variant="h6">Jobs Board</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.6)',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#3b82f6'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b82f6'
            }
          }}
        >
          <Tab 
            value="browse" 
            icon={<SearchIcon />} 
            iconPosition="start"
            label="Browse" 
          />
          <Tab 
            value="applications" 
            icon={
              <Badge badgeContent={applications.length} color="primary">
                <SendIcon />
              </Badge>
            } 
            iconPosition="start"
            label="Applied" 
          />
          <Tab 
            value="profile" 
            icon={<PersonIcon />} 
            iconPosition="start"
            label="Profile" 
          />
          <Tab 
            value="employer" 
            icon={<BusinessIcon />} 
            iconPosition="start"
            label="Employer" 
          />
        </Tabs>

        <DialogContent sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CyberSpinner />
            </Box>
          ) : (
            <>
              {activeTab === 'browse' && renderBrowseTab()}
              {activeTab === 'applications' && renderApplicationsTab()}
              {activeTab === 'profile' && renderProfileTab()}
              {activeTab === 'employer' && renderEmployerTab()}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Job Detail Dialog */}
      {selectedJob && (
        <JobDetail
          job={selectedJob}
          open={!!selectedJob}
          onClose={() => setSelectedJob(null)}
          onApply={handleApply}
          hasApplied={applications.some(a => a.jobId === selectedJob.id)}
        />
      )}

      {/* Company Registration Dialog */}
      {showCompanyRegistration && (
        <CompanyRegistration
          open={showCompanyRegistration}
          onClose={() => setShowCompanyRegistration(false)}
          onRegistered={handleCompanyRegistered}
        />
      )}

      {/* Job Post Form Dialog */}
      {showJobPostForm && (
        <JobPostForm
          open={showJobPostForm}
          onClose={() => setShowJobPostForm(false)}
          onPosted={handleJobPosted}
        />
      )}
    </>
  );
}

export default JobsBoard;
